import { useQuery, useStatus } from '@powersync/react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';

import {
  queries,
  search,
  type CaptureDestinationRow,
  type SearchHit,
  type WorkspaceSummary,
} from '@flow/sync';
import { SearchResults, type SearchResultGroup } from '@flow/ui/components';

import { useToday } from '../capture/use-today.js';
import type { LocalDatabase } from '../db/powersync.js';
import { t } from '../i18n/es.js';
import { useDetailHost } from '../list/use-detail-host.jsx';
import { useSession } from '../session.jsx';
import { useSetViewHeader } from '../shell/AppLayout.jsx';

/**
 * The search page (REC-06 basic, design D6 of f1-trash-search): a field, the results of the local
 * full-text index grouped by kind with the snippet highlighted as text, and the detail panel for
 * the chosen one (`?tarea=`), with the same actions as in the lists. The text lives in `?q=`.
 */

/** Order of the groups (design D6). */
const KIND_ORDER = ['task', 'project', 'note', 'page', 'link', 'item', 'file'];
/** Results read per search: the page shows 50 per group, then «Mostrar más». */
const LIMIT = 200;
/** Wait after the last key before searching. */
const DEBOUNCE_MS = 120;

export function SearchPage() {
  const { local, user } = useSession();
  const { q, tarea } = useSearch({ from: '/app/search' });
  const setHeader = useSetViewHeader();
  useEffect(() => {
    setHeader({ title: t.search.title });
  }, [setHeader]);
  if (!local || !user) return null;
  return <Search local={local} userId={user.id} term={q ?? ''} openId={tarea ?? null} />;
}

interface CurrentRow {
  id: string;
  title: string;
  kind: string;
  status: string | null;
}

function Search({
  local,
  userId,
  term,
  openId,
}: {
  local: LocalDatabase;
  userId: string;
  term: string;
  openId: string | null;
}) {
  const navigate = useNavigate();
  const today = useToday(userId);
  const [draft, setDraft] = useState(term);
  // A text from elsewhere (the palette) replaces the field; our own debounced one does not.
  const [sent, setSent] = useState(term);
  const [seenTerm, setSeenTerm] = useState(term);
  if (seenTerm !== term) {
    setSeenTerm(term);
    if (term !== sent) setDraft(term);
  }
  const [hits, setHits] = useState<SearchHit[]>([]);
  const [took, setTook] = useState<number | null>(null);

  const workspaceQuery = queries.personalWorkspace();
  const { data: workspaces } = useQuery<WorkspaceSummary>(
    workspaceQuery.sql,
    workspaceQuery.params,
  );
  const workspaceId = workspaces[0]?.id ?? '';
  const stagesQuery = queries.workspaceStages(workspaceId);
  const { data: stages } = useQuery<CaptureDestinationRow>(stagesQuery.sql, stagesQuery.params);

  // The text goes to the URL after a pause, so the page survives a reload (spec «Página»).
  useEffect(() => {
    if (draft === term) return undefined;
    const timer = setTimeout(() => {
      setSent(draft);
      void navigate({
        to: '/search',
        search: { ...(draft ? { q: draft } : {}), ...(openId ? { tarea: openId } : {}) },
        replace: true,
      });
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [draft, term, openId, navigate]);

  // Searched again after each sync: a fresh database (WebKit keeps it in memory, a first sign-in)
  // fills after the page has opened.
  const syncedAt = useStatus().lastSyncedAt?.getTime() ?? 0;
  useEffect(() => {
    let cancelled = false;
    const started = performance.now();
    void search(local.db, term, LIMIT).then((result) => {
      if (cancelled) return;
      setHits(result);
      setTook(term.trim() ? Math.round(performance.now() - started) : null);
    });
    return () => {
      cancelled = true;
    };
  }, [local.db, term, syncedAt]);

  // Titles, status and deletions of the shown results follow the database.
  const ids = useMemo(() => hits.map((hit) => hit.block_id), [hits]);
  const currentQuery = queries.blocksByIds(ids);
  const { data: current } = useQuery<CurrentRow>(currentQuery.sql, currentQuery.params);

  const groups = useMemo<SearchResultGroup[]>(() => {
    const byId = new Map(current.map((row) => [row.id, row]));
    const out = new Map<string, SearchResultGroup>();
    for (const hit of hits) {
      const row = byId.get(hit.block_id);
      if (!row) continue;
      const group = out.get(row.kind) ?? {
        key: row.kind,
        label: t.search.groups[row.kind] ?? row.kind,
        items: [],
      };
      group.items.push({
        id: row.id,
        title: row.title,
        done: row.status === 'done',
        parts: hit.snippet,
      });
      out.set(row.kind, group);
    }
    return [...out.values()].sort((a, b) => KIND_ORDER.indexOf(a.key) - KIND_ORDER.indexOf(b.key));
  }, [hits, current]);
  const count = groups.reduce((total, group) => total + group.items.length, 0);

  const openTask = (id: string | null) =>
    void navigate({
      to: '/search',
      search: { ...(term ? { q: term } : {}), ...(id ? { tarea: id } : {}) },
      replace: !id,
    });

  const actions = useDetailHost({
    userId,
    local,
    workspaceId,
    stages,
    period: undefined,
    type: undefined,
    active: [],
    highlightsEnabled: false,
    today,
    openTaskId: openId,
    onOpenTask: openTask,
    onClosed: () => {
      // Back to the result it was opened from.
      if (openId) {
        document
          .querySelector<HTMLElement>(`[data-block-id="${openId}"] .search-results__open`)
          ?.focus();
      }
    },
  });

  return (
    <div className="page search-page">
      <input
        type="search"
        className="search-page__field"
        aria-label={t.search.field}
        placeholder={t.search.placeholder}
        autoFocus={!openId}
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
      />
      {actions.notice ? (
        <p className="list-view__notice" role="status">
          {actions.notice}
        </p>
      ) : null}
      {!term.trim() ? <p className="muted">{t.search.prompt}</p> : null}
      {took !== null ? (
        <p className="muted" data-testid="search-summary" data-hits={count} data-ms={took}>
          {t.search.results(count)} · {t.search.took(took)}
        </p>
      ) : null}
      {term.trim() && took !== null && count === 0 ? (
        <p className="empty-state">{t.search.empty(term)}</p>
      ) : null}
      <SearchResults
        groups={groups}
        onOpen={(id) => openTask(id)}
        openId={openId}
        labels={{
          heading: t.search.heading,
          open: t.search.open,
          done: t.search.done,
          more: t.search.more,
        }}
      />
      {actions.dialogs}
    </div>
  );
}
